import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../ui/dialog";
import { api } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Building2, Plus, Loader2 } from "lucide-react";

const STATUS_STYLES = {
  pending: "text-amber-600 border-amber-500/40 bg-amber-500/10",
  approved: "text-emerald-600 border-emerald-500/40 bg-emerald-500/10",
  rejected: "text-red-600 border-red-500/40 bg-red-500/10",
};

export default function PortalVendorRequests() {
  const { toast } = useToast();
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ tool_name: "", reason: "" });

  const load = () => api.get("/employee-portal/vendor-requests").then((r) => setItems(r.data.requests));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.tool_name || !form.reason) return;
    setSaving(true);
    try {
      await api.post("/employee-portal/vendor-requests", form);
      toast({ title: "Request submitted" });
      setOpen(false);
      setForm({ tool_name: "", reason: "" });
      load();
    } finally { setSaving(false); }
  };

  if (!items) return <div className="h-40 rounded-xl bg-muted/40 animate-pulse" />;

  return (
    <div className="space-y-4" data-testid="portal-vendor-requests">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Vendor Requests</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5" data-testid="portal-new-vendor-request-btn"><Plus className="w-3.5 h-3.5" /> New request</Button>
          </DialogTrigger>
          <DialogContent data-testid="portal-vendor-request-dialog">
            <DialogHeader><DialogTitle>Request a new tool/vendor</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Tool / vendor name</Label>
                <Input value={form.tool_name} onChange={(e) => setForm({ ...form, tool_name: e.target.value })} placeholder="e.g. Notion" data-testid="portal-vendor-request-tool-input" />
              </div>
              <div>
                <Label>Why do you need it?</Label>
                <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={3} data-testid="portal-vendor-request-reason-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={saving} data-testid="portal-vendor-request-submit-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Submit request"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-2">
        {items.map((v) => (
          <Card key={v.id} data-testid={`portal-vendor-request-row-${v.id}`}>
            <CardContent className="p-4 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="text-[13px] font-medium">{v.tool_name}</div>
                  <div className="text-[12px] text-muted-foreground">{v.reason}</div>
                </div>
              </div>
              <Badge variant="outline" className={`flex-shrink-0 capitalize ${STATUS_STYLES[v.status] || ""}`}>{v.status}</Badge>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <p className="text-[13px] text-muted-foreground">No requests submitted yet.</p>}
      </div>
    </div>
  );
}
